import { styled } from 'styled-components';
import { ReactSVG } from 'react-svg';
import { useEffect, useState } from 'react';
import Button, { buttonType } from '../../general/Button';
import colors from '../../../constants/colors';
import { PhotoFolder } from '../../../types/models/Photos';
import styleConstants from '../../../constants/styleConstants';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Cross from '../../icons/Cross';
import Tick from '../../icons/Tick';

interface ModalProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleValidate: () => Promise<void>;
  isProcessing?: boolean;
  progress?: {
    processed: number;
    total: number;
    currentUrl?: string;
  };
  errors?: string[];
}

const RemplissageAutoConfirmModal: React.FC<ModalProps> = ({
  visible,
  setVisible,
  handleValidate,
  isProcessing = false,
  progress,
  errors = []
}) => {
    const {t} = useTranslation(['common']);
    const [localProcessing, setLocalProcessing] = useState(false);

    const handleValidateClick = async () => {
      setLocalProcessing(true);
      try {
        await handleValidate();
      } catch (error) {
        console.error('Auto-fill validation error:', error);
      } finally {
        setLocalProcessing(false);
      }
    };

    const processing = isProcessing || localProcessing;
    const showProgress = processing && progress && progress.total > 0;

  return createPortal(
    <Container $show={visible} >
        
      <FormTopContainer>
        {!processing && (
          <CloseIcon onClick={() => setVisible(false)}>
            <ReactSVG src="/svg/cross.svg" />
          </CloseIcon>
        )}

        <div
            style={{
                width: "100%",
                marginTop: "1em"
            }}
        >
            <UpperTitle>
                {processing 
                  ? t('photos.remplissageAuto.processingTitle', 'Processing Photos...')
                  : t('photos.remplissageAuto.confirmationTitle')
                }
            </UpperTitle>
        </div>
      </FormTopContainer>
      <FormContainer>
        <div style={{
            marginBottom: "3em"
        }}>
            {processing ? (
              <div>
                <Title>{t('photos.remplissageAuto.processingMessage', 'Please wait while we process your photos...')}</Title>
                {showProgress && (
                  <ProgressContainer>
                    <ProgressBar>
                      <ProgressFill $percentage={(progress.processed / progress.total) * 100} />
                    </ProgressBar>
                    <ProgressText>
                      {progress.processed} / {progress.total} {t('photos.remplissageAuto.photosProcessed', 'photos processed')}
                    </ProgressText>
                    {progress.currentUrl && (
                      <CurrentPhotoText>
                        {t('photos.remplissageAuto.processingPhoto', 'Processing')}: {progress.currentUrl.split('/').pop()?.substring(0, 30)}...
                      </CurrentPhotoText>
                    )}
                  </ProgressContainer>
                )}
                {errors.length > 0 && (
                  <ErrorContainer>
                    <ErrorTitle>{t('photos.remplissageAuto.errorsFound', 'Some photos could not be processed')}:</ErrorTitle>
                    {errors.slice(0, 3).map((error, index) => (
                      <ErrorItem key={index}>{error}</ErrorItem>
                    ))}
                    {errors.length > 3 && (
                      <ErrorItem>{t('photos.remplissageAuto.moreErrors', 'And {count} more errors...', { count: errors.length - 3 })}</ErrorItem>
                    )}
                  </ErrorContainer>
                )}
              </div>
            ) : (
              <Title>{t('photos.remplissageAuto.confirmationBody')}</Title>
            )}
        </div>
        {!processing && (
          <ButtonContainer>
            <ActionButton
              $type={buttonType.themed}
              style={{
                  width: "40%",
                  height: "3rem",
                  backgroundColor: "#98A2B3",
              }}
              onClick={() => setVisible(false)}
            >
              <Cross />
              {t('photos.remplissageAuto.rejectionActionText')}
            </ActionButton>
            <ActionButton
              $type={buttonType.themed}
              style={{
                  width: "40%",
                  height: "3rem"
              }}
              onClick={handleValidateClick}
              disabled={localProcessing}
            >
              <Tick />
              {t('photos.remplissageAuto.confirmationActionText')}
            </ActionButton>
          </ButtonContainer>
        )}
      </FormContainer>
    </Container>,
    document.body,
  );
};

const Container = styled.div<{ $show: boolean }>`
  width: 100%;
  height: 100%;

  display: ${({ $show }) => ($show ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: center;
  align-items: center;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  background-color: rgba(52, 64, 84, 0.7);
`;

const FormTopContainer = styled.div`
  width: 90%;
  max-width: 700px;
  height: auto;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  padding: 0 0 0 18px;

  box-sizing: border-box;

  position: relative;

  background-color: ${colors.white};
  color: ${colors.black};

  border-radius: 8px 8px 0 0;

  @media (max-width: ${styleConstants.breakpointLg}) {
    width: 320px;
  }
  @media (max-width: ${styleConstants.breakpointSm}) {
    padding: 0.8rem;
  }
`;

const FormContainer = styled.div`
  width: 90%;
  max-width: 700px;

  display: flex;
  flex-direction: column;

  background-color: ${colors.white};
  color: ${colors.black};

  box-sizing: border-box;
  border-radius: 0 0 8px 8px;
  padding: 24px;

  @media (max-width: ${styleConstants.breakpointLg}) {
    width: 320px;
  }
  @media (max-width: ${styleConstants.breakpointSm}) {
    padding: 1rem;
  }
`;

const Title = styled.div`
  font-family: 'DM Sans';
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
  text-align: center;
  white-space: pre-line;
  color: ${colors.gray900};

  @media (max-width: 480px) {
      line-height: 20px;
      font-size: 16px;
      white-space: normal;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
    
  top: 20px;
  right: 24px;

  cursor: pointer;

  @media (max-width: ${styleConstants.breakpointLg}) {
    top: 12px;
    width: 12px;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: solid 1px ${colors.gray300};
  border-radius: 8px;

  box-sizing: border-box;

  margin: 0 0 20px;

  font-family: 'DM Sans';
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  gap: 1rem;

  @media (max-width: ${styleConstants.breakpointSm}) {
    flex-direction: column;
    width: 100%;
    align-items: center;

    button {
      width: 100% !important;
    }
  }
`;

const Text = styled.div`
  display: flex;
  margin: 20px 0 0;

  font-family: 'DM Sans';
  font-size: 14px;
  font-weight: 500;
  text-align: left;
`;

const UpperTitle = styled.div`
  font-family: 'Playfair Display';
  font-size: 28px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
  white-space: pre-line;

  @media (max-width: 480px) {
      line-height: 20px;
      font-size: 20px;
      white-space: normal;
  }
`;


const ActionButton = styled.button<{
  $type: buttonType;
  disabled?: boolean;
  $themeColor?: string;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 18px;
  box-sizing: border-box;
  border: 1px solid;
  border-radius: 8px;

  font-style: normal;
  font-weight: 500;
  font-size: 16px;

  gap: 16px;

  background-color: ${(props) =>
    props.disabled
      ? props.$type === buttonType.white ||
        props.$type === buttonType['white-noborder']
        ? colors.white
        : colors.gray400
      : props.$type === buttonType.black
      ? colors.black
      : props.$type === buttonType.themed
      ? props.$themeColor || colors.green
      : props.$type === buttonType.red
      ? colors.error600
      : colors.white};
  color: ${(props) =>
    props.$type === buttonType.black ||
    props.$type === buttonType.themed ||
    props.$type === buttonType.red
      ? colors.white
      : colors.black};
  border: ${(props) =>
    props.$type === buttonType.white ? 'solid 1px ' + colors.gray300 : 'none'};

  cursor: ${(props) => (props.disabled ? 'auto' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

    @media (max-width: 480px) {
      font-size: 14px;
    }
`;

const ProgressContainer = styled.div`
  margin-top: 2rem;
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${colors.gray200};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  width: ${props => props.$percentage}%;
  height: 100%;
  background-color: ${colors.green};
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.div`
  font-family: 'DM Sans';
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray600};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const CurrentPhotoText = styled.div`
  font-family: 'DM Sans';
  font-size: 12px;
  font-weight: 400;
  color: ${colors.gray500};
  text-align: center;
  font-style: italic;
`;

const ErrorContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const ErrorTitle = styled.div`
  font-family: 'DM Sans';
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 0.5rem;
`;

const ErrorItem = styled.div`
  font-family: 'DM Sans';
  font-size: 12px;
  font-weight: 400;
  color: #991b1b;
  margin-bottom: 0.25rem;
`;

export default RemplissageAutoConfirmModal;
